<script lang="ts">
	import type { RFCanvasMeta } from '@src/types/reframe';
	import RFAccordion from '@src/core/elements/rf-accordion/rf-accordion.svelte';
	import classNames from 'classnames';
	import RfInspectorControl from './rf-inspector-control.svelte';
	import inspectorStore from '@store/inspector.svelte';

	let {
		item,
		mode = 'canvas',
		oncontrolclick = (_: MouseEvent, _canvas: RFCanvasMeta) => {},
		dragstart = () => {},
		dragend = () => {},
		addAllLabel = 'Add all',
	} = $props();

	const onDragStart = (e: DragEvent, canvas: RFCanvasMeta) => {
		// set the data transfer data to the canvas object
		e?.dataTransfer?.setData('text/plain', JSON.stringify(canvas));
		dragstart(e);
	};

	const onDragEnd = (e: DragEvent) => {
		// clear the transfer data
		e?.dataTransfer?.clearData();
		dragend(e);
	};

	const addAllCanvases = () => {
		// add all canvases to the inspector
		item.canvases.forEach((canvas: RFCanvasMeta) => {
			inspectorStore.addSelectedCanvas(canvas as RFCanvasMeta & { key: string });
		});
	};

	const showAddAllButton = $derived(mode !== 'canvas' && item.canvases.length > 1);
</script>

<div class="rf-inspector-card">
	<RFAccordion title={item.module.name} description={item.canvases.length.toString().padStart(2, '0')} defaultOpen>
		<div class="rf-inspector-card__content">
			{#if showAddAllButton}
				<button class="rf-inspector-card__button" onclick={() => addAllCanvases()}>
					{addAllLabel}
				</button>
			{/if}
			<ul class="rf-inspector-card__canvases">
				{#each item.canvases as canvas}
					<li class="canvas-card">
						{#await canvas.thumbnail then value}
							<img
								class={classNames('canvas-card__image', { 'canvas-card__image--has-legend': canvas.hasLegend })}
								draggable="true"
								ondragstart={(e: DragEvent) => onDragStart(e, canvas)}
								ondragend={onDragEnd}
								src={value}
								alt=""
								srcset=""
							/>
						{/await}
						<RfInspectorControl
							icon={mode === 'canvas' ? 'look-up' : 'plus'}
							ariaLabel={mode === 'canvas' ? 'View in inspector' : `Add to ${mode} inspector`}
							ariaHaspopup="dialog"
							ariaControls="rf-inspector"
							onclick={(e: MouseEvent) => oncontrolclick(e, canvas)}
						/>
					</li>
				{/each}
			</ul>
		</div>
	</RFAccordion>
</div>

<style lang="scss" global>
	.rf-inspector-card {
		display: block;
		font-family: var(--rf-font-family);
		margin: 0;
		padding: var(--rf-spacing-s, 8px);
		background-color: var(--rf-inspector-card-bg-color, rgba(50, 50, 50, 0.5));
		list-style: none;
		border-radius: var(--rf-spacing-xs, 4px);
		color: var(--rf-secondary-font-color, #fff);
		text-decoration: none;

		&__content {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			justify-content: space-between;
			gap: var(--rf-spacing-s, 8px);
		}

		&__button {
			cursor: pointer;
			background: var(--rf-inspector-control-background-color, rgba(35, 35, 35, 0.5));
			border: 1px solid var(--rf-inspector-control-border-color, rgba(150, 150, 150, 0.1));
			border-radius: var(--rf-spacing-xs, 4px);
			padding: var(--rf-spacing-xs, 4px) var(--rf-spacing-s, 8px);
			color: var(--rf-inspector-control-color, #fff);

			&:hover {
				background: var(--rf-inspector-control-hover-color, rgba(255, 255, 255, 0.1));
			}
		}

		&__canvases {
			padding: 0;
			margin: 0;
			width: 100%;
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			gap: 1rem;
		}

		.canvas-card {
			min-height: 180px;
			position: relative;
			display: flex;
			list-style: none;
			align-items: flex-end;
			border-radius: var(--rf-spacing-xs, 4px);
			padding: var(--rf-spacing-xs, 4px);
			overflow: hidden;

			&__image {
				position: absolute;
				top: 0;
				right: 0;
				display: block;
				width: 100%;
				height: 100%;
				cursor: move;
				object-fit: cover;
			}
		}
	}
</style>
